import React, { FormEvent, ChangeEvent, useState } from "react"
import { navigate } from "gatsby"
import "./contactForm.scss"

export type ContactFormComponent = {
  useNetlifyForm?: boolean
  actionURL: string
}

type HandleChangeEvent = (
  event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>
) => void
type HandleSubmitEvent = (event: FormEvent<HTMLFormElement>) => void

type FormComponent = {
  handleChange: HandleChangeEvent
  loading?: boolean
}

type NetlifyFormComponent = {
  handleChange: HandleChangeEvent
  handleSubmit?: HandleSubmitEvent
  actionURL: string
} & FormComponent

const NetlifyForm: React.FC<NetlifyFormComponent> = ({
  children,
  handleChange,
  handleSubmit,
  actionURL,
}) => {
  return (
    <form
      name="contact"
      method="post"
      action={actionURL}
      data-netlify="true"
      data-netlify-honeypot="bot-field"
      onSubmit={handleSubmit}
    >
      {/* The `form-name` hidden field is required to support form submissions without JavaScript */}
      <input type="hidden" name="form-name" value="contact" />
      <div hidden>
        <label>
          Don’t fill this out:{" "}
          <input name="bot-field" onChange={handleChange} />
        </label>
      </div>
      {children}
    </form>
  )
}

const SimpleFormData: React.FC<FormComponent> = ({ handleChange, loading }) => {
  return (
    <>
      <div className="field">
        <label className="label font-head font-sm sub-head" htmlFor={"name"}>
          Your name
        </label>
        <div className="input-control">
          <input
            className="input"
            type={"text"}
            name={"name"}
            onChange={handleChange}
            id={"name"}
            required={true}
          />
        </div>
      </div>
      <div className="field">
        <label className="label font-head font-sm" htmlFor={"email"}>
          Email
        </label>
        <div className="input-control">
          <input
            className="input"
            type={"email"}
            name={"email"}
            onChange={handleChange}
            id={"email"}
            required={true}
          />
        </div>
      </div>
      <div className="field">
        <label className="label font-head font-sm" htmlFor={"message"}>
          Message
        </label>
        <div className="input-control">
          <textarea
            className="textarea"
            name={"message"}
            onChange={handleChange}
            id={"message"}
            required={true}
          />
        </div>
      </div>
      <div className="field">
        <button className="btn btn-dark btn-md" type="submit">
          {loading ? "Submitting" : "Send"}
        </button>
      </div>
    </>
  )
}

const ContactForm: React.FC<ContactFormComponent> = ({
  useNetlifyForm = true,
  actionURL = process.env.CONTACTFORM_ENDPOINT,
}) => {
  const [inputValue, setInputValue] = useState({})
  const [loading, setLoading] = useState<boolean>(false)

  // encode obj params to uri string
  const encode = (data): string => {
    return Object.keys(data)
      .map(
        (key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key])
      )
      .join("&")
  }

  const handleSubmit: HandleSubmitEvent = (e) => {
    setLoading(true)
    e.preventDefault()
    const form = e.target
    fetch(actionURL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({
        "form-name": form.getAttribute("name"),
        ...inputValue,
      }),
    })
      .then(() => {
        setLoading(false)
        navigate("/contact/thanks")
      })
      .catch((error) => alert(error))
  }

  const handleChange: HandleChangeEvent = (e) => {
    setInputValue({ ...inputValue, [e.target.name]: e.target.value })
  }

  // render different form to use netlify forms
  if (useNetlifyForm) {
    return (
      <NetlifyForm
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        actionURL={actionURL}
      >
        <SimpleFormData handleChange={handleChange} loading={loading} />
      </NetlifyForm>
    )
  }
  return (
    <form
      name="contact"
      method="post"
      action={actionURL}
      onSubmit={handleSubmit}
    >
      <SimpleFormData handleChange={handleChange} loading={loading} />
    </form>
  )
}
export default ContactForm
